import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Copy, Check, BarChart3, Zap, AlertTriangle, ArrowUpRight, ArrowDownRight, X, Code, ExternalLink, CheckCircle, Settings, Code2 } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import HeaderBar from '@/components/HeaderBar';
import TeamInvitationBanner from '@/components/TeamInvitationBanner';
import { useConfirm } from '@/components/ConfirmProvider';
import { LineChart, Line, AreaChart, Area, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skeleton loader for website cards
const WebsiteCardSkeleton = () => (
	<Card className="website-card-new animate-pulse">
		<CardContent className="p-6">
			<div className="flex justify-between items-start mb-4">
			   	<div className="h-5 bg-gray-200 rounded w-48"></div>
				<div className="h-4 w-4 bg-gray-200 rounded"></div>
			</div>
			<div className="h-16 bg-gray-100 rounded my-5"></div>
			<div className="space-y-2">
				<div className="h-8 bg-gray-200 rounded w-16"></div>
				<div className="h-4 bg-gray-100 rounded w-32"></div>
				<div className="h-4 bg-gray-100 rounded w-12"></div>
			</div>
		</CardContent>
	</Card>
);

const DashboardPage = ({ user, token, logout }) => {
	const confirm = useConfirm();
	const [searchParams, setSearchParams] = useSearchParams();
	const [data, setData] = useState({ websites: [], usage: null, stats: {}, user_teams: [] });
	const [loading, setLoading] = useState(true);
	const [showAddWebsite, setShowAddWebsite] = useState(false);
	const [showLimitWarning, setShowLimitWarning] = useState(false);
	const [domain, setDomain] = useState('');
	const [trackingModal, setTrackingModal] = useState({ show: false, website: null });
	const [scriptCopied, setScriptCopied] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [verificationStatus, setVerificationStatus] = useState(null);
	const navigate = useNavigate();
	const fetchedRef = useRef(false);

	// Get selected team from URL or localStorage
	const getInitialTeam = () => {
		const urlTeam = searchParams.get('team');
		if (urlTeam) {
			localStorage.setItem('selectedTeam', urlTeam);
			return urlTeam;
		}
		return localStorage.getItem('selectedTeam') || 'personal';
	};

	const [selectedTeam, setSelectedTeam] = useState(getInitialTeam);

	// Handle team change
	const handleTeamChange = (teamValue) => {
		setSelectedTeam(teamValue);
		localStorage.setItem('selectedTeam', teamValue);
		
		// Update URL
		if (teamValue === 'personal') {
			setSearchParams({ team: 'personal' });
		} else {
			setSearchParams({ team: teamValue });
		}
		
		// Refetch dashboard with new team filter
		fetchedRef.current = false;
		setLoading(true);
		fetchDashboardWithTeam(teamValue);
	};

		// Single API call to get all dashboard data
	const fetchDashboardWithTeam = async (team) => {
		try {
			const params = team && team !== 'all' ? { team } : {};
			const res = await axios.get(`${API}/dashboard`, {
					headers: { Authorization: `Bearer ${token}` },
					params
			});
			setData(res.data);
		} catch (err) {
			console.error('Failed to load dashboard', err);
			toast.error('Failed to load dashboard');
		} finally {
			setLoading(false);
		}
	};

	const fetchDashboard = async () => {
			await fetchDashboardWithTeam(selectedTeam);
	};

	useEffect(() => {
		if (fetchedRef.current) return;
		fetchedRef.current = true;
		fetchDashboard();
	}, [token]);

	const refreshDashboard = () => {
		fetchedRef.current = false;
		setLoading(true);
		fetchDashboard();
	};

	const handleAddWebsite = async (e) => {
		e.preventDefault();
		// Normalize domain: strip protocol and www
		const cleanDomain = domain.trim().toLowerCase()
				.replace(/^https?:\/\//, '')
				.replace(/^www\./, '')
				.replace(/\/+$/, '');
		if (!cleanDomain) {
			toast.error('Please enter a valid domain');
			return;
		}
		try {
			const res = await axios.post(`${API}/websites`, { domain: cleanDomain }, {
					headers: { Authorization: `Bearer ${token}` }
			});
			toast.success('Website added!');
			setDomain('');
			setShowAddWebsite(false);
			setTrackingModal({ show: true, website: res.data });
			setScriptCopied(false);
			setVerificationStatus(null);
			refreshDashboard();
		} catch (err) {
			toast.error(err.response?.data?.detail || 'Failed to add website');
		}
	};

	const handleDeleteWebsite = async (id) => {
		const site = data.websites.find((w) => w.id === id);
		const ok = await confirm({
			title: 'Delete this website?',
			description: (
				<>
					{site?.domain && (
						<span className="block font-semibold text-gray-700 mb-1 break-all">
							{site.domain}
						</span>
					)}
					Tracking stops immediately. Past pageview data is kept and can be exported later.
				</>
			),
			confirmLabel: 'Delete website',
		});
		if (!ok) return;
		try {
			await axios.delete(`${API}/websites/${id}`, {
					headers: { Authorization: `Bearer ${token}` }
			});
			toast.success('Website deleted');
			refreshDashboard();
		} catch (err) {
			toast.error('Failed to delete website');
		}
	};

	const getTrackingScript = (website) => {
		if (!website) return '';
		return `<script defer data-tracking-id="${website.tracking_id}" src="${process.env.REACT_APP_BACKEND_URL}/api/js/script.js"></script>`;
	};

	const copyScript = () => {
		navigator.clipboard.writeText(getTrackingScript(trackingModal.website));
		setScriptCopied(true);
		setTimeout(() => setScriptCopied(false), 3000);
		toast.success('Copied!');
	};

	const verifyInstallation = async () => {
		if (!trackingModal.website) return;
		setVerifying(true);
		setVerificationStatus(null);
		
		try {
			const res = await axios.get(`${API}/analytics/${trackingModal.website.id}/metrics`, {
				headers: { Authorization: `Bearer ${token}` },
				params: { period: '24h' }
			});
			
			if (res.data.total_pageviews > 0 || res.data.unique_visitors > 0) {
				setVerificationStatus('success');
				toast.success('Tracking verified!');
			} else {
				setVerificationStatus('pending');
				toast.info('No data yet. Visit your site first.');
			}
		} catch {
			setVerificationStatus('error');
			toast.error('Verification failed');
		} finally {
			setVerifying(false);
		}
	};

	const closeModal = () => {
			setTrackingModal({ show: false, website: null });
			setVerificationStatus(null);
	};

	const calcChange = (curr, prev) => {
			if (prev === 0) return { pct: curr > 0 ? 100 : 0, up: curr >= 0 };
			const change = ((curr - prev) / prev) * 100;
			return { pct: Math.abs(Math.round(change)), up: change >= 0 };
	};

	const { websites, usage, stats, user_teams = [] } = data;

	// Get dynamic title based on selected team
	const getTitle = () => {
			if (selectedTeam === 'personal') {
					return 'My Websites';
			}
			const team = user_teams.find(t => t.id === selectedTeam);
			return team ? `${team.name} Sites` : 'My Websites';
	};

	return (
		<div className="page-container">
			<HeaderBar 
				user={user} 
				logout={logout}
				userTeams={user_teams}
				selectedTeam={selectedTeam}
				onTeamChange={handleTeamChange}
			/>
				
			<div className="dashboard-wrapper">
				<div className="dashboard-container-centered">
					{/* Header */}
					<div className="dashboard-header-new">
						<h1 className="dashboard-title">{getTitle()}</h1>
						{selectedTeam === 'personal' && (
							<Button 
								onClick={() => {
									if (usage && !usage.can_add_more) {
										setShowLimitWarning(true);
										setShowAddWebsite(false);
									} else {
										setShowAddWebsite(!showAddWebsite);
										setShowLimitWarning(false);
									}
								}} 
								className="add-website-btn-new"
							>
								<Plus className="icon-sm" />
								Add Website
							</Button>
						)}
					</div>

					{/* Team Invitation Banner */}
					<TeamInvitationBanner
							token={token}
							onInvitationHandled={refreshDashboard}
					/>

					{/* Subscription Cancelled banner — shown when the user's Stripe
					    subscription has been fully cancelled (subscription_status is
					    kept in sync by the Stripe webhook). Personal view only —
					    team members shouldn't see the site owner's billing state. */}
					{usage?.subscription_status === 'canceled' && selectedTeam === 'personal' && (
						<div className="limit-warning-banner">
							<AlertTriangle className="icon-sm" />
							<div className="warning-content">
								<h3>Subscription Cancelled</h3>
								<p>Your subscription has been cancelled. You're now on the free tier with limited features.</p>
							</div>
							<Button onClick={() => navigate('/subscription')} className="upgrade-btn upgrade-btn--amber">
								Resume Subscription
							</Button>
						</div>
					)}

					{/* Limit Warning */}
					{showLimitWarning && selectedTeam === 'personal' && (
						<div className="limit-warning-banner">
							<AlertTriangle className="icon-sm" />
							<div className="warning-content">
								<h3>Website Limit Reached</h3>
								<p>You've reached your limit of {usage?.plan_limit} websites.</p>
							</div>
							<Button onClick={() => navigate('/subscription')} className="upgrade-btn">
								<Zap className="icon-sm" /> Upgrade
							</Button>
						</div>
					)}

					{/* Pageview Limit Warning */}
					{usage?.pageview_limit_reached && selectedTeam === 'personal' && (
						<div className="limit-warning-banner">
							<AlertTriangle className="icon-sm" />
							<div className="warning-content">
								<h3>Pageview Limit Reached</h3>
								<p>
									You've used {usage.monthly_pageviews_used.toLocaleString()} of your {usage.page_limit.toLocaleString()} monthly pageviews.
									New pageviews are no longer being recorded.
								</p>
							</div>
							<Button onClick={() => navigate('/subscription')} className="upgrade-btn">
								<Zap className="icon-sm" /> Upgrade
							</Button>
						</div>
					)}

					{/* Add Website Form */}
					{showAddWebsite && selectedTeam === 'personal' && (
						<Card className="add-website-card">
							<CardContent className="p-6">
								<form onSubmit={handleAddWebsite} className="add-website-form">
									<Input
										type="text"
										placeholder="yourdomain.com"
										value={domain}
										onChange={(e) => setDomain(e.target.value)}
										required
									/>
									<Button type="submit">Add Website</Button>
								</form>
							</CardContent>
						</Card>
					)}

					{/* Loading State - Skeleton */}
					{loading ? (
						<div className="websites-grid-centered">
								{[1, 2, 3].map(i => <WebsiteCardSkeleton key={i} />)}
						</div>
					) : websites.length === 0 ? (
						<div className="empty-state">
							<BarChart3 className="empty-icon" />
							<h2>{selectedTeam === 'personal' ? 'No websites yet' : 'No shared websites'}</h2>
							<p>{selectedTeam === 'personal' ? 'Add your first website to start tracking' : 'This team has no websites shared with you'}</p>
						</div>
					) : (
						<div className="websites-grid-centered">
							{/* All Sites Summary Card - spans 2 rows */}
							{websites.length >= 2 && (() => {
								const totalVisitors = Object.values(stats).reduce((sum, s) => sum + (s.visitors || 0), 0);
								const prevTotalVisitors = Object.values(stats).reduce((sum, s) => sum + (s.prev_visitors || 0), 0);
								const totalPageviews = Object.values(stats).reduce((sum, s) => sum + (s.pageviews || 0), 0);
								const prevTotalPageviews = Object.values(stats).reduce((sum, s) => sum + (s.prev_pageviews || 0), 0);
								const totalVisits = Object.values(stats).reduce((sum, s) => sum + (s.visits || s.visitors || 0), 0);
								const prevTotalVisits = Object.values(stats).reduce((sum, s) => sum + (s.prev_visits || s.prev_visitors || 0), 0);
								const viewsPerVisit = totalVisits > 0 ? (totalPageviews / totalVisits).toFixed(2) : '0.00';
								const prevViewsPerVisit = prevTotalVisits > 0 ? (prevTotalPageviews / prevTotalVisits).toFixed(2) : '0.00';
								const visitorsChange = calcChange(totalVisitors, prevTotalVisitors);
								const visitsChange = calcChange(totalVisits, prevTotalVisits);
								const pageviewsChange = calcChange(totalPageviews, prevTotalPageviews);
								const vpvChange = calcChange(parseFloat(viewsPerVisit), parseFloat(prevViewsPerVisit));
								const fmt = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n.toString();

								const mergedChart = Array.from({ length: 24 }, (_, i) => ({
										value: Object.values(stats).reduce((sum, s) => sum + ((s.chart_data?.[i]?.value) || 0), 0)
								}));

								return (
									<Card className="website-card-new all-sites-card" data-testid="all-sites-card">
										<CardContent className="p-6 all-sites-content">
											<div className="all-sites-icon">
												<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
											</div>
											<h3 className="all-sites-title" data-testid="all-sites-title">All sites</h3>
											<div className="all-sites-chart">
												<ResponsiveContainer width="100%" height="100%">
													<AreaChart data={mergedChart}>
														<defs>
															<linearGradient id="allSitesGradient" x1="0" y1="0" x2="0" y2="1">
																<stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
																<stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
															</linearGradient>
														</defs>
														<Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#allSitesGradient)" dot={false} />
													</AreaChart>
												</ResponsiveContainer>
											</div>
											<div className="all-sites-stats-grid">
												<div className="all-sites-stat" data-testid="all-sites-visitors">
													<span className="all-sites-stat-label">Unique visitors</span>
													<span className="all-sites-stat-value">{fmt(totalVisitors)}</span>
													<span className={`all-sites-change ${visitorsChange.up ? 'up' : 'down'}`}>
															{visitorsChange.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
															{visitorsChange.pct}%
													</span>
												</div>
												<div className="all-sites-stat" data-testid="all-sites-visits">
													<span className="all-sites-stat-label">Total visits</span>
													<span className="all-sites-stat-value">{fmt(totalVisits)}</span>
													<span className={`all-sites-change ${visitsChange.up ? 'up' : 'down'}`}>
															{visitsChange.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
															{visitsChange.pct}%
													</span>
												</div>
												<div className="all-sites-stat" data-testid="all-sites-pageviews">
													<span className="all-sites-stat-label">Total pageviews</span>
													<span className="all-sites-stat-value">{fmt(totalPageviews)}</span>
													<span className={`all-sites-change ${pageviewsChange.up ? 'up' : 'down'}`}>
															{pageviewsChange.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
															{pageviewsChange.pct}%
													</span>
												</div>
												<div className="all-sites-stat" data-testid="all-sites-vpv">
													<span className="all-sites-stat-label">Views per visit</span>
													<span className="all-sites-stat-value">{viewsPerVisit}</span>
													<span className={`all-sites-change ${vpvChange.up ? 'up' : 'down'}`}>
															{vpvChange.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
															{vpvChange.pct}%
													</span>
												</div>
											</div>
										</CardContent>
									</Card>
								);
							})()}


							{websites.map((site) => {
								const s = stats[site.id] || { visitors: 0, prev_visitors: 0, chart_data: [] };
								const change = calcChange(s.visitors, s.prev_visitors);
								
								return (
									<Card 
										key={site.id} 
										className="website-card-new"
										onClick={() => navigate(`/analytics/${site.id}`)}
										style={{ cursor: 'pointer' }}
									>
										<CardContent className="p-6">
											<div className="website-card-header">
												{/* Domain is a real Link (not just card-onclick) so users
												    get proper link affordances: right-click → open in new
												    tab, keyboard focus, hover underline. The whole card
												    still navigates on click (via the outer Card onClick),
												    but the visible indigo underlined domain removes the
												    "is this clickable?" ambiguity people had before. */}
												<Link
													to={`/analytics/${site.id}`}
													onClick={(e) => e.stopPropagation()}
													className="website-domain"
													style={{
														color: '#4f46e5',
														textDecoration: 'none',
														cursor: 'pointer',
													}}
													onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
													onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
													title={`Open analytics for ${site.domain}`}
												>
													{site.domain}
												</Link>
												{/* Icon action group — tracking snippet, settings, delete.
												    Each icon wrapped in a Radix Tooltip so hovering
												    shows a styled label (native title="" is slow/ugly).
												    All three stop propagation so they don't also fire
												    the outer Card's whole-card navigate handler. */}
												<TooltipProvider delayDuration={200}>
													<div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
														<Tooltip>
															<TooltipTrigger asChild>
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		setTrackingModal({ show: true, website: site });
																	}}
																	className="delete-icon-btn"
																	aria-label={`Show tracking code for ${site.domain}`}
																>
																	<Code2 className="w-4 h-4 text-gray-400 hover:text-indigo-600" />
																</button>
															</TooltipTrigger>
															<TooltipContent>View tracking code</TooltipContent>
														</Tooltip>

														<Tooltip>
															<TooltipTrigger asChild>
																<Link
																	to={`/settings/${site.id}`}
																	onClick={(e) => e.stopPropagation()}
																	className="delete-icon-btn"
																	aria-label={`Settings for ${site.domain}`}
																	style={{ display: 'inline-flex', alignItems: 'center' }}
																>
																	<Settings className="w-4 h-4 text-gray-400 hover:text-indigo-600" />
																</Link>
															</TooltipTrigger>
															<TooltipContent>Site settings</TooltipContent>
														</Tooltip>

														<Tooltip>
															<TooltipTrigger asChild>
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		handleDeleteWebsite(site.id);
																	}}
																	className="delete-icon-btn"
																	aria-label={`Delete ${site.domain}`}
																>
																	<Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
																</button>
															</TooltipTrigger>
															<TooltipContent>Delete site</TooltipContent>
														</Tooltip>
													</div>
												</TooltipProvider>
											</div>
												
											{/* Mini Chart */}
											<div className="mini-chart" style={{ height: '60px', margin: '20px 0' }}>
												{s.chart_data?.length > 0 ? (
													<ResponsiveContainer width="100%" height="100%">
														<LineChart data={s.chart_data}>
															<Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={false} />
														</LineChart>
													</ResponsiveContainer>
												) : (
													<div style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
														<BarChart3 className="w-8 h-8" />
													</div>
												)}
											</div>
											
											{/* Stats */}
											<div className="stats-section">
												<div className="visitor-count">{s.visitors}</div>
												<div className="visitor-label">visitors in last 24h</div>
												<div className={`change-indicator ${change.up ? 'positive' : 'negative'}`}>
														{change.up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
														{change.pct}%
												</div>
											</div>
										</CardContent>
									</Card>
								);
							})}
						</div>
					)}
				</div>
			</div>

			{/* Tracking Modal */}
			{trackingModal.show && trackingModal.website && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
						{/* Header */}
						<div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-500 to-purple-600">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
									<Code className="w-5 h-5 text-white" />
								</div>
								<div>
									<h3 className="text-lg font-semibold text-white">Website Added!</h3>
									<p className="text-sm text-white/80">{trackingModal.website.domain}</p>
								</div>
							</div>
							<button onClick={closeModal} className="text-white/80 hover:text-white">
								<X className="w-5 h-5" />
							</button>
						</div>

						{/* Body */}
						<div className="p-6">
							{/* Step 1 */}
							<div className="mb-6">
								<div className="flex items-center gap-2 mb-3">
									<div className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-semibold">1</div>
									<h4 className="font-semibold text-gray-900">Install Script</h4>
								</div>
								<p className="text-sm text-gray-600 mb-4 ml-8">
										Add this to your site's <code className="bg-gray-100 px-1.5 py-0.5 rounded text-indigo-600">&lt;head&gt;</code>
								</p>
								<div className="ml-8 relative">
									<div className="bg-gray-900 rounded-lg p-4 pr-12 overflow-x-auto">
										<code className="text-sm text-green-400 font-mono break-all">
											{getTrackingScript(trackingModal.website)}
										</code>
									</div>
									<button onClick={copyScript} className="absolute top-3 right-3 p-2 bg-gray-700 hover:bg-gray-600 rounded-md">
											{scriptCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-300" />}
									</button>
								</div>
								<div className="ml-8 mt-3 text-sm">
									<a href="https://plausible.io/docs/plausible-script" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
										Installation guides <ExternalLink className="w-3 h-3" />
									</a>
								</div>
							</div>

							<div className="border-t my-6"></div>

							{/* Step 2 */}
							<div>
								<div className="flex items-center gap-2 mb-3">
									<div className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-semibold">2</div>
									<h4 className="font-semibold text-gray-900">Verify</h4>
								</div>
								<p className="text-sm text-gray-600 mb-4 ml-8">Check if tracking is working.</p>

								{verificationStatus === 'success' && (
									<div className="ml-8 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
										<CheckCircle className="w-5 h-5 text-green-500" />
										<span className="text-sm text-green-700">Tracking verified!</span>
									</div>
								)}
								{verificationStatus === 'pending' && (
									<div className="ml-8 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
										<AlertTriangle className="w-5 h-5 text-yellow-500" />
										<span className="text-sm text-yellow-700">No data yet. Visit your site first.</span>
									</div>
								)}
								{verificationStatus === 'error' && (
									<div className="ml-8 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
										<X className="w-5 h-5 text-red-500" />
										<span className="text-sm text-red-700">Verification failed.</span>
									</div>
								)}

								<div className="ml-8">
									<Button onClick={verifyInstallation} disabled={verifying} className="bg-indigo-600 hover:bg-indigo-700 text-white">
										{verifying ? (
												<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>Verifying...</>
										) : (
												<><CheckCircle className="w-4 h-4 mr-2" />Verify Installation</>
										)}
									</Button>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
							<p className="text-xs text-gray-500">Find this code in settings anytime.</p>
							<div className="flex gap-3">
								<Button variant="outline" onClick={closeModal}>Close</Button>
								<Button onClick={() => { closeModal(); navigate(`/analytics/${trackingModal.website.id}`); }} className="bg-indigo-600 hover:bg-indigo-700 text-white">
										Go to Dashboard
								</Button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DashboardPage;
